import { useEffect, useState } from 'react'

/**
 * Thin read-only bridge to the CookieYes CMP loaded from index.html.
 *
 * CookieYes owns the banner, geo-targeting and Google Consent Mode signals.
 * This module only exposes the current consent state for a category and
 * notifies subscribers when it changes, so in-app SDKs (Amplitude, GA, Clarity,
 * chat widgets) can be gated on it.
 *
 * index.html decides per hostname whether CookieYes runs (`window.__tmConsent`):
 *  - `managed`: a CookieYes site key exists for this host, consent comes from the CMP
 *  - `blocked`: EU/UK host without a site key yet, nothing non-essential may run
 *  - `open`: host outside the CMP scope (non-EEA mirror domains), behaves as before
 *
 * Sources of truth on a managed host, in order:
 *  1. window.getCkyConsent() - available once banner.js has executed
 *  2. the `cookieyes-consent` cookie - available on repeat visits before banner.js runs
 *  3. nothing -> treated as "no consent"
 *
 * Visitors outside the banner-targeted regions receive an automatic grant from
 * CookieYes (cookie written, `cookieyes_consent_update` dispatched), so the gate
 * opens for them without any banner interaction.
 */

export type CookieYesCategory =
  | 'necessary'
  | 'functional'
  | 'analytics'
  | 'performance'
  | 'advertisement'

export type ConsentPolicy = 'managed' | 'blocked' | 'open'

export interface CookieYesConsent {
  activeLaw: string
  categories: Partial<Record<CookieYesCategory, boolean>>
  isUserActionCompleted: boolean
  consentID: string
  languageCode: string
}

interface CookieYesConsentUpdateDetail {
  accepted?: string[]
  rejected?: string[]
}

declare global {
  interface Window {
    getCkyConsent?: () => CookieYesConsent | undefined
    __tmConsent?: { policy?: ConsentPolicy; siteKey?: string }
  }
}

export const COOKIEYES_COOKIE_NAME = 'cookieyes-consent'
export const COOKIEYES_CONSENT_UPDATE_EVENT = 'cookieyes_consent_update'
export const COOKIEYES_BANNER_LOAD_EVENT = 'cookieyes_banner_load'

const RECHECK_EVENTS = ['focus', 'visibilitychange'] as const

/**
 * Cookies and storage keys written by the SDKs gated on each category.
 * Cleared when the visitor withdraws consent for that category, because the
 * SDKs themselves do not remove what they already set.
 */
const CATEGORY_COOKIE_PATTERNS: Partial<Record<CookieYesCategory, RegExp>> = {
  analytics: /^(_ga|_gid|_gat|_gcl_|AMP_|_clck|_clsk|_hjSession|_hjid)/,
  functional: /^(hubspotutk|__hstc|__hssc|__hssrc|messagesUtk|hs-messages-|__lc)/,
}

const CATEGORY_STORAGE_PATTERNS: Partial<Record<CookieYesCategory, RegExp>> = {
  analytics: /^(AMP_|tmca:feature-flags:device-id$|_clck|_clsk)/,
  functional: /^(hs-|__lc|@@lc)/,
}

/**
 * The CookieYes loader refuses to run on hosts outside the registered domain
 * (it throws on `localhost`), so in local development the gate would never
 * open. Treat consent as granted there; staging/production builds are
 * unaffected because `import.meta.env.DEV` is false.
 */
const isLocalDevBypass = (): boolean =>
  import.meta.env.DEV &&
  typeof window !== 'undefined' &&
  ['localhost', '127.0.0.1'].includes(window.location.hostname)

export function getConsentPolicy(): ConsentPolicy {
  if (typeof window === 'undefined') return 'managed'
  const policy = window.__tmConsent?.policy
  return policy === 'open' || policy === 'blocked' ? policy : 'managed'
}

function readConsentCookie(): Record<string, string> | null {
  if (typeof document === 'undefined') return null
  try {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${COOKIEYES_COOKIE_NAME}=([^;]*)`))
    if (!match) return null
    return Object.fromEntries(
      match[1]
        .split(',')
        .filter(Boolean)
        .map((pair) => {
          const [key, ...rest] = pair.split(':')
          return [key, rest.join(':')]
        })
    )
  } catch {
    return null
  }
}

export function hasCookieYesConsent(category: CookieYesCategory): boolean {
  if (typeof window === 'undefined') return false
  if (category === 'necessary') return true
  if (isLocalDevBypass()) return true

  const policy = getConsentPolicy()
  if (policy === 'open') return true
  if (policy === 'blocked') return false

  try {
    if (typeof window.getCkyConsent === 'function') {
      const consent = window.getCkyConsent()
      if (consent?.categories) {
        return consent.categories[category] === true
      }
    }
  } catch {
    // fall through to cookie
  }

  const cookie = readConsentCookie()
  return cookie?.[category] === 'yes'
}

/**
 * Subscribe to consent changes for a category. The callback is invoked
 * immediately with the current state and then on every CookieYes event.
 * Returns an unsubscribe function.
 */
export function subscribeCookieYesConsent(
  category: CookieYesCategory,
  callback: (granted: boolean) => void
): () => void {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return () => undefined
  }

  const emitCurrent = () => callback(hasCookieYesConsent(category))

  if (getConsentPolicy() !== 'managed') {
    emitCurrent()
    return () => undefined
  }

  const onConsentUpdate = (event: Event) => {
    const detail = (event as CustomEvent<CookieYesConsentUpdateDetail>).detail
    if (Array.isArray(detail?.accepted)) {
      callback(detail.accepted.includes(category))
      return
    }
    emitCurrent()
  }

  const onBannerLoad = (event: Event) => {
    const detail = (event as CustomEvent<CookieYesConsent>).detail
    if (detail?.categories) {
      callback(detail.categories[category] === true)
      return
    }
    emitCurrent()
  }

  document.addEventListener(COOKIEYES_CONSENT_UPDATE_EVENT, onConsentUpdate)
  document.addEventListener(COOKIEYES_BANNER_LOAD_EVENT, onBannerLoad)
  RECHECK_EVENTS.forEach((name) => window.addEventListener(name, emitCurrent))

  emitCurrent()

  return () => {
    document.removeEventListener(COOKIEYES_CONSENT_UPDATE_EVENT, onConsentUpdate)
    document.removeEventListener(COOKIEYES_BANNER_LOAD_EVENT, onBannerLoad)
    RECHECK_EVENTS.forEach((name) => window.removeEventListener(name, emitCurrent))
  }
}

function cookieDomainCandidates(hostname: string): (string | undefined)[] {
  const labels = hostname.split('.')
  const candidates: (string | undefined)[] = [undefined]
  for (let i = 0; i < labels.length - 1; i++) {
    const domain = labels.slice(i).join('.')
    candidates.push(domain, `.${domain}`)
  }
  return candidates
}

function expireCookie(name: string): void {
  const expiry = 'expires=Thu, 01 Jan 1970 00:00:00 GMT'
  cookieDomainCandidates(window.location.hostname).forEach((domain) => {
    const domainPart = domain ? `; domain=${domain}` : ''
    document.cookie = `${name}=; ${expiry}; path=/${domainPart}`
  })
}

function cookieNames(): string[] {
  return document.cookie
    .split(';')
    .map((pair) => pair.trim().split('=')[0])
    .filter(Boolean)
}

/**
 * Remove cookies and storage entries of the SDKs covered by a category.
 * Safe to call repeatedly; unknown cookies are left untouched.
 */
export function clearCategoryStorage(category: CookieYesCategory): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return

  const cookiePattern = CATEGORY_COOKIE_PATTERNS[category]
  if (cookiePattern) {
    try {
      cookieNames()
        .filter((name) => cookiePattern.test(name))
        .forEach(expireCookie)
    } catch {
      // cookies unavailable
    }
  }

  const storagePattern = CATEGORY_STORAGE_PATTERNS[category]
  if (storagePattern) {
    ;[window.localStorage, window.sessionStorage].forEach((storage) => {
      try {
        Object.keys(storage)
          .filter((key) => storagePattern.test(key))
          .forEach((key) => storage.removeItem(key))
      } catch {
        // storage unavailable
      }
    })
  }
}

/**
 * Clears SDK cookies/storage for every category the visitor withdraws.
 * Mount once near the app root.
 */
export function useConsentWithdrawalCleanup(): void {
  useEffect(() => {
    if (typeof document === 'undefined' || getConsentPolicy() !== 'managed') return

    const onConsentUpdate = (event: Event) => {
      const detail = (event as CustomEvent<CookieYesConsentUpdateDetail>).detail
      const rejected = Array.isArray(detail?.rejected) ? detail.rejected : []
      rejected.forEach((category) => clearCategoryStorage(category as CookieYesCategory))
    }

    document.addEventListener(COOKIEYES_CONSENT_UPDATE_EVENT, onConsentUpdate)
    return () => document.removeEventListener(COOKIEYES_CONSENT_UPDATE_EVENT, onConsentUpdate)
  }, [])
}

export function useCookieYesConsent(category: CookieYesCategory): boolean {
  const [granted, setGranted] = useState<boolean>(() => hasCookieYesConsent(category))

  useEffect(() => subscribeCookieYesConsent(category, setGranted), [category])

  return granted
}

export const useAnalyticsConsent = (): boolean => useCookieYesConsent('analytics')

export const useFunctionalConsent = (): boolean => useCookieYesConsent('functional')
